import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/Components/ui/tooltip";
import { LucideProps, PackageCheck, Check, CircleCheck, FileChartLine, LayoutTemplate, Milestone, SquareStack, User, ArrowDown, ArrowUp, Pencil, NotebookTabs, Workflow, Layers, ChevronUp, ChevronRight, Trash2, LayoutDashboard, CopyCheck, Undo2, Home, CopyPlus, ChartBarStacked, Users, Eye, ThumbsUp, UserX, ChartGantt, ZoomIn, ZoomOut, ArrowRight, ArrowLeft, MousePointerClick, Download, Plus, RotateCw, MessageCircleQuestion, SlidersHorizontal, 
     Bold, List, ListOrdered, Italic, TextQuote, Pilcrow, Strikethrough, Heading1, X, Save, Underline, Minus, CalendarRange
} from "lucide-react";
import React from "react";
import PackageMilestone from "./PackageMilestone";

export interface TooltipIconProps {
    icon: React.ForwardRefExoticComponent<Omit<LucideProps, "ref"> & React.RefAttributes<SVGSVGElement>>;
    tooltip?: string;
    delayDuration?: number;
    className?: string;
}

function TooltipIcon({ icon, tooltip, className, delayDuration }: TooltipIconProps) {
    return (
        <TooltipProvider delayDuration={delayDuration ?? 300}>
            <Tooltip>
                <TooltipTrigger asChild>
                    {React.createElement(icon, { className })}
                </TooltipTrigger>
                <TooltipContent className="flex flex-col bg-background">
                    <div className="flex flex-row text-muted-foreground place-content-between">
                        <span className="p-2">{tooltip ? tooltip : "Unknown Icon"}</span>
                    </div>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
}

export interface IconProps {
    className?: string;
    tooltip?: string;
    delayDuration?: number;
}

export default TooltipIcon;

export const DeliverableIcon: React.FC<IconProps> = ({ className, tooltip, delayDuration }) => {
    return (
        <TooltipIcon icon={PackageCheck} tooltip={tooltip ? tooltip : "Deliverable"} className={className} delayDuration={delayDuration} />
    );
};

export const CheckIcon: React.FC<IconProps> = ({ className, tooltip, delayDuration }) => {
    return (
        <TooltipIcon icon={Check} tooltip={tooltip ? tooltip : "Completed"} className={className} delayDuration={delayDuration} />
    );
};

export const CompletedIcon: React.FC<IconProps> = ({ className, tooltip, delayDuration }) => {
    return (
        <TooltipIcon icon={CircleCheck} tooltip={tooltip ? tooltip : "Completed"} className={className} delayDuration={delayDuration} />
    );
};

export const StatusReportIcon: React.FC<IconProps> = ({ className, tooltip, delayDuration }) => {
    return (
        <TooltipIcon icon={FileChartLine} tooltip={tooltip ? tooltip : "Display on Status Report"} className={className} delayDuration={delayDuration} />
    );
};

export const CharterIcon: React.FC<IconProps> = ({ className, tooltip, delayDuration }) => {
    return (
        <TooltipIcon icon={LayoutTemplate} tooltip={tooltip ? tooltip : "Display on Charter"} className={className} delayDuration={delayDuration} />
    );
};

export const MilestoneIcon: React.FC<IconProps> = ({ className, tooltip, delayDuration }) => {
    return (
        <TooltipIcon icon={Milestone} tooltip={tooltip ? tooltip : "Milestone"} className={className} delayDuration={delayDuration} />
    );
};


export const DependenciesIcon: React.FC<IconProps> = ({ className, tooltip, delayDuration }) => {
    return (
        <TooltipIcon icon={SquareStack} tooltip={tooltip ? tooltip : "Dependencies"} className={className} delayDuration={delayDuration} />
    );
};

export const UserIcon: React.FC<IconProps> = ({ className, tooltip, delayDuration }) => {
    return (
        <TooltipIcon icon={User} tooltip={tooltip ? tooltip : "Assigned to you"} className={className} delayDuration={delayDuration} />
    );
}

export const ArrowUpIcon: React.FC<IconProps> = ({ className, tooltip, delayDuration }) => {
    return (
        <TooltipIcon icon={ArrowUp} tooltip={tooltip ? tooltip : "Close All"} className={className} delayDuration={delayDuration} />
    );
}

export const ArrowDownIcon: React.FC<IconProps> = ({ className, tooltip, delayDuration }) => {
    return (
        <TooltipIcon icon={ArrowDown} tooltip={tooltip ? tooltip : "Open All"} className={className} delayDuration={delayDuration} />
    );
}

export const EditIcon: React.FC<IconProps> = ({ className, tooltip, delayDuration }) => {
    return (
        <TooltipIcon icon={Pencil} tooltip={tooltip ? tooltip : "Edit"} className={className} delayDuration={delayDuration} />
    );
}

export const TaskIcon: React.FC<IconProps> = ({ className, tooltip, delayDuration }) => {
    return (
        <TooltipIcon icon={NotebookTabs} tooltip={tooltip ? tooltip : "Task"} className={className} delayDuration={delayDuration} />
    );
}

export const WorkPackageIcon: React.FC<IconProps> = ({ className, tooltip, delayDuration }) => {
    return (
        <TooltipIcon icon={Layers} tooltip={tooltip ? tooltip : "Work Package"} className={className} delayDuration={delayDuration} />
    );
}

export const WorkstreamIcon: React.FC<IconProps> = ({ className, tooltip, delayDuration }) => {
    return (
        <TooltipIcon icon={Workflow} tooltip={tooltip ? tooltip : "Workstream"} className={className} delayDuration={delayDuration} />
    );
}

export const ChevronUpIcon: React.FC<IconProps> = ({ className, tooltip, delayDuration }) => {
    return (
        <TooltipIcon icon={ChevronUp} tooltip={tooltip ? tooltip : "Collapse"} className={className} delayDuration={delayDuration} />
    );
}

export const ChevronRightIcon: React.FC<IconProps> = ({ className, tooltip, delayDuration }) => {
    return (
        <TooltipIcon icon={ChevronRight} tooltip={tooltip ? tooltip : "Expand"} className={className} delayDuration={delayDuration} />
    );
}

export const DeleteIcon: React.FC<IconProps> = ({ className, tooltip, delayDuration }) => {
    return (
        <TooltipIcon icon={Trash2} tooltip={tooltip ? tooltip : "Delete"} className={className} delayDuration={delayDuration} />
    );
}

export const DashboardIcon: React.FC<IconProps> = ({ className, tooltip, delayDuration }) => {
    return (
        <TooltipIcon icon={LayoutDashboard} tooltip={tooltip ? tooltip : "Display On Dashboard"} className={className} delayDuration={delayDuration} />
    );
}

export const MultiSelectIcon: React.FC<IconProps> = ({ className, tooltip, delayDuration }) => {
    return (
        <TooltipIcon icon={CopyCheck} tooltip={tooltip ? tooltip : "Multi Select"} className={className} delayDuration={delayDuration} />
    );
}

export const UndoIcon: React.FC<IconProps> = ({ className, tooltip, delayDuration }) => {
    return (
        <TooltipIcon icon={Undo2} tooltip={tooltip ? tooltip : "Undo"} className={className} delayDuration={delayDuration} />
    );
}

export const ProjectIcon: React.FC<IconProps> = ({ className, tooltip, delayDuration }) => {
    return (
        <TooltipIcon icon={Home} tooltip={tooltip ? tooltip : "Project"} className={className} delayDuration={delayDuration} />
    );
}

export const MultiAddIcon: React.FC<IconProps> = ({ className, tooltip, delayDuration }) => {
    return (
        <TooltipIcon icon={CopyPlus} tooltip={tooltip ? tooltip : "Add Multiple Tasks"} className={className} delayDuration={delayDuration} />
    );
}

export const ChartIcon: React.FC<IconProps> = ({ className, tooltip, delayDuration }) => {
    return (
        <TooltipIcon icon={ChartBarStacked} tooltip={tooltip ? tooltip : "Show Progress Chart"} className={className} delayDuration={delayDuration} />
    );
}

export const ManageTeamIcon: React.FC<IconProps> = ({ className, tooltip, delayDuration }) => {
    return (
        <TooltipIcon icon={Users} tooltip={tooltip ? tooltip : "Manage Team"} className={className} delayDuration={delayDuration} />
    );
}

export const SeenIcon: React.FC<IconProps> = ({ className, tooltip, delayDuration }) => {
    return (
        <TooltipIcon icon={Eye} tooltip={tooltip ? tooltip : "Seen"} className={className} delayDuration={delayDuration} />
    );
}

export const AcknowledgedIcon: React.FC<IconProps> = ({ className, tooltip, delayDuration }) => {
    return (
        <TooltipIcon icon={ThumbsUp} tooltip={tooltip ? tooltip : "Acknowledged"} className={className} delayDuration={delayDuration} />
    );
}

export const UnassignedIcon: React.FC<IconProps> = ({ className, tooltip, delayDuration }) => {
    return (
        <TooltipIcon icon={UserX} tooltip={tooltip ? tooltip : "Unassigned"} className={className} delayDuration={delayDuration} />
    );
}

export const GanttChartIcon: React.FC<IconProps> = ({ className, tooltip, delayDuration }) => {
    return (
        <TooltipIcon icon={ChartGantt} tooltip={tooltip ? tooltip : "Show Gantt Chart"} className={className} delayDuration={delayDuration} />
    );
};

export const ZoomInIcon: React.FC<IconProps> = ({ className, tooltip, delayDuration }) => {
    return (
        <TooltipIcon icon={ZoomIn} tooltip={tooltip ? tooltip : "Zoom In"} className={className} delayDuration={delayDuration} />
    );
};

export const ZoomOutIcon: React.FC<IconProps> = ({ className, tooltip, delayDuration }) => {
    return (
        <TooltipIcon icon={ZoomOut} tooltip={tooltip ? tooltip : "Zoom Out"} className={className} delayDuration={delayDuration} />
    );
};

export const ArrowRightIcon: React.FC<IconProps> = ({ className, tooltip, delayDuration }) => {
    return (
        <TooltipIcon icon={ArrowRight} tooltip={tooltip ? tooltip : "Assign Selected Users"} className={className} delayDuration={delayDuration} />
    );
};

export const ArrowLeftIcon: React.FC<IconProps> = ({ className, tooltip, delayDuration }) => {
    return (
        <TooltipIcon icon={ArrowLeft} tooltip={tooltip ? tooltip : "Remove Users"} className={className} delayDuration={delayDuration} />
    );
}

export const SelectedIcon: React.FC<IconProps> = ({ className, tooltip, delayDuration }) => {
    return (
        <TooltipIcon icon={MousePointerClick} tooltip={tooltip ? tooltip : "Selected"} className={className} delayDuration={delayDuration} />
    );
};

export const DownloadIcon: React.FC<IconProps> = ({ className, tooltip, delayDuration }) => {
    return (
        <TooltipIcon icon={Download} tooltip={tooltip ? tooltip : "Export"} className={className} delayDuration={delayDuration} />
    );
}

export const AddIcon: React.FC<IconProps> = ({ className, tooltip, delayDuration }) => {
    return (
        <TooltipIcon icon={Plus} tooltip={tooltip ? tooltip : "Add"} className={className} delayDuration={delayDuration} />
    );
};

export const PackageMilestoneIcon: React.FC<IconProps> = ({ className, tooltip, delayDuration }) => {
    return (
        <TooltipIcon icon={PackageMilestone} tooltip={tooltip ? tooltip : "Deliverable Milestone"} className={className} delayDuration={delayDuration} />
    );
};

export const RefreshIcon: React.FC<IconProps> = ({ className, tooltip, delayDuration }) => {
    return (
        <TooltipIcon icon={RotateCw} tooltip={tooltip ? tooltip : "Reload Data"} className={className} delayDuration={delayDuration} />
    );
}

export const HelpIcon: React.FC<IconProps> = ({ className, tooltip, delayDuration }) => {
    return (
        <TooltipIcon icon={MessageCircleQuestion} tooltip={tooltip ? tooltip : "Help"} className={className} delayDuration={delayDuration} />
    );
}

export const ShowControlsIcon: React.FC<IconProps> = ({ className, tooltip, delayDuration }) => {
    return (
        <TooltipIcon icon={SlidersHorizontal} tooltip={tooltip ? tooltip : "Show Controls"} className={className} delayDuration={delayDuration} />
    );
}

export const BoldIcon: React.FC<IconProps> = ({ className, tooltip, delayDuration }) => {
    return (
        <TooltipIcon icon={Bold} tooltip={tooltip ? tooltip : "Bold"} className={className} delayDuration={delayDuration} />
    );
}

export const ItalicIcon: React.FC<IconProps> = ({ className, tooltip, delayDuration }) => {
    return (
        <TooltipIcon icon={Italic} tooltip={tooltip ? tooltip : "Italic"} className={className} delayDuration={delayDuration} />
    );
}
export const UnderlineIcon: React.FC<IconProps> = ({ className, tooltip, delayDuration }) => {
    return (
        <TooltipIcon icon={Underline} tooltip={tooltip ? tooltip : "Underline"} className={className} delayDuration={delayDuration} />
    );
}

export const Heading1Icon: React.FC<IconProps> = ({ className, tooltip, delayDuration }) => {
    return (
        <TooltipIcon icon={Heading1} tooltip={tooltip ? tooltip : "Heading 1"} className={className} delayDuration={delayDuration} />
    );
}

export const PilcrowIcon: React.FC<IconProps> = ({ className, tooltip, delayDuration }) => {
    return (
        <TooltipIcon icon={Pilcrow} tooltip={tooltip ? tooltip : "Paragraph"} className={className} delayDuration={delayDuration} />
    );
}

export const StrikethroughIcon: React.FC<IconProps> = ({ className, tooltip, delayDuration }) => {
    return (
        <TooltipIcon icon={Strikethrough} tooltip={tooltip ? tooltip : "Strikethrough"} className={className} delayDuration={delayDuration} />
    );
}

export const SaveIcon: React.FC<IconProps> = ({ className, tooltip, delayDuration }) => {
    return (
        <TooltipIcon icon={Save} tooltip={tooltip ? tooltip : "Save"} className={className} delayDuration={delayDuration} />
    );
}

export const CloseIcon: React.FC<IconProps> = ({ className, tooltip, delayDuration }) => {
    return (
        <TooltipIcon icon={X} tooltip={tooltip ? tooltip : "Close"} className={className} delayDuration={delayDuration} />
    );
}

export const MinusIcon: React.FC<IconProps> = ({ className, tooltip, delayDuration }) => {
    return (
        <TooltipIcon icon={Minus} tooltip={tooltip ? tooltip : "Minus"} className={className} delayDuration={delayDuration} />
    );
}
export const ListIcon: React.FC<IconProps> = ({ className, tooltip, delayDuration }) => {
    return (
        <TooltipIcon icon={List} tooltip={tooltip ? tooltip : "Bullet List"} className={className} delayDuration={delayDuration} />
    );
}

export const OrderedListIcon: React.FC<IconProps> = ({ className, tooltip, delayDuration }) => {
    return (
        <TooltipIcon icon={ListOrdered} tooltip={tooltip ? tooltip : "Numbered List"} className={className} delayDuration={delayDuration} />
    );
}

export const TextQuoteIcon: React.FC<IconProps> = ({ className, tooltip, delayDuration }) => {
    return (
        <TooltipIcon icon={TextQuote} tooltip={tooltip ? tooltip : "Block Quote"} className={className} delayDuration={delayDuration} />
    );
}

export const CalendarRangeIcon: React.FC<IconProps> = ({ className, tooltip, delayDuration }) => {
    return (
        <TooltipIcon icon={CalendarRange} tooltip={tooltip ? tooltip : "Date Range"} className={className} delayDuration={delayDuration} />
    );
}